import { useEffect, useRef } from "react";

// Reusable guard for the ubiquitous
//   useEffect(() => { if (user) fetch(); }, [user])
// pattern in every data context. React StrictMode double-invokes effects
// on mount in dev, and every upstream context re-render gives us a new
// `user` object reference even when the identity hasn't changed. Left
// unchecked, that fires all context fetches (customers, invoices,
// inventory × 5, vendors, credit-notes, users, etc.) multiple times per
// second, which is what the request log shows.
//
// This hook remembers the last user id we fetched for and skips the
// fetch until either:
//   - the user id actually changes (log out & back in as someone else)
//   - a manual reset() (e.g. after a successful create/delete)
export function useFetchOncePerUser(user, fetchFn) {
  const fetchedForUserId = useRef(null);
  useEffect(() => {
    const uid = user?.id || user?._id || null;
    if (!uid) { fetchedForUserId.current = null; return; }
    if (fetchedForUserId.current === uid) return;
    fetchedForUserId.current = uid;
    fetchFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  return () => { fetchedForUserId.current = null; };
}
